import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Check, Download, Loader2, RotateCcw, X } from "lucide-react";
import { CertBadge, downloadCert } from "./CertBadges";

export default function TrainingQuiz({ topic, onBack }) {
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/training/quizzes/${topic}`).then((r) => { setQuiz(r.data); setAnswers(r.data.questions.map(() => null)); })
      .catch((err) => toast.error(err?.response?.data?.detail || "Could not load quiz"));
  }, [topic]);
  if (!quiz) return <div className="p-10 grid place-items-center"><Loader2 className="animate-spin" /></div>;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/training/quizzes/${topic}/submit`, { answers });
      setResult(r.data);
      if (r.data.passed) toast.success(`Badge earned: ${r.data.badge}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Submit failed"); }
    finally { setBusy(false); }
  };
  const retake = () => { setResult(null); setAnswers(quiz.questions.map(() => null)); };

  return (
    <div className="max-w-3xl mx-auto py-2" data-testid={`training-quiz-${topic}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <button onClick={onBack} className="text-xs inline-flex items-center gap-1 hover:underline" data-testid="training-quiz-back"><ArrowLeft size={12} /> Back to video</button>
        {quiz.certified_at && !result && <CertBadge badge={`${quiz.title} Certified`} date={quiz.certified_at} testid="training-quiz-existing-badge" />}
      </div>
      <div className="font-display font-bold text-xl">{quiz.title} — quick check</div>
      <div className="text-sm text-[var(--ll-ink-soft)] mb-4">Answer all 3 correctly to earn the <b>{quiz.title} Certified</b> badge. Retake as often as you like.</div>
      <ol className="space-y-4">
        {quiz.questions.map((q, i) => (
          <Question key={i} i={i} q={q} value={answers[i]} result={result?.results?.[i]}
            onPick={(v) => !result && setAnswers((a) => a.map((x, j) => (j === i ? v : x)))} />
        ))}
      </ol>
      {result ? <Outcome result={result} topic={topic} onRetake={retake} /> : (
        <button onClick={submit} disabled={busy || answers.some((a) => a === null)}
          className="mt-5 px-4 py-2 text-sm text-white bg-[var(--ll-ink)] disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="training-quiz-submit">
          {busy && <Loader2 size={14} className="animate-spin" />} Submit answers
        </button>
      )}
    </div>
  );
}

function Question({ i, q, value, result, onPick }) {
  return (
    <li className="border p-4" style={{ borderColor: result === undefined ? "var(--ll-border)" : result ? "#059669" : "#DC2626" }} data-testid={`training-quiz-q-${i}`}>
      <div className="font-medium text-sm mb-2 flex items-center gap-2">
        {i + 1}. {q.q}
        {result === true && <Check size={14} className="text-[#059669]" data-testid={`training-quiz-correct-${i}`} />}
        {result === false && <X size={14} className="text-[#DC2626]" data-testid={`training-quiz-wrong-${i}`} />}
      </div>
      <div className="space-y-1.5">
        {q.options.map((o, j) => (
          <label key={j} className={`flex items-center gap-2 text-sm px-2 py-1.5 cursor-pointer transition-colors ${value === j ? "bg-[var(--ll-mint)]" : "hover:bg-[var(--ll-mint)]"}`}>
            <input type="radio" name={`q${i}`} checked={value === j} onChange={() => onPick(j)} data-testid={`training-quiz-q-${i}-opt-${j}`} />
            {o}
          </label>
        ))}
      </div>
    </li>
  );
}

function Outcome({ result, topic, onRetake }) {
  const { user } = useAuth();
  if (result.passed) {
    return (
      <div className="mt-5 p-5 text-center" style={{ background: "#F5F3FF" }} data-testid="training-quiz-passed">
        <div className="text-sm mb-3">3 of 3 correct — nice work!</div>
        <CertBadge badge={result.badge} date={result.certified_at} large testid="training-quiz-badge" />
        <div className="mt-4">
          <button onClick={() => downloadCert(topic, null, user?.name)}
            className="text-sm px-4 py-2 text-white bg-[var(--ll-ink)] inline-flex items-center gap-1.5 hover:opacity-90" data-testid="training-quiz-download-cert">
            <Download size={14} /> Download certificate (PDF)
          </button>
        </div>
        <div className="text-[11px] text-[var(--ll-ink-muted)] mt-3">Your badge now shows on the Team page.</div>
      </div>
    );
  }
  return (
    <div className="mt-5 p-4 border-l-4 flex flex-wrap items-center justify-between gap-3" style={{ background: "#FEF2F2", borderColor: "var(--ll-error)" }} data-testid="training-quiz-failed">
      <div className="text-sm"><b data-testid="training-quiz-score">{result.score} of {result.total}</b> correct. You need all 3 to certify — rewatch the chapter and try again.</div>
      <button onClick={onRetake} className="text-sm px-3 py-1.5 border bg-white inline-flex items-center gap-1.5" style={{ borderColor: "var(--ll-border)" }} data-testid="training-quiz-retake">
        <RotateCcw size={13} /> Retake
      </button>
    </div>
  );
}
